/**
 * Integrytas Group - Common UI (desktop)
 *
 * Builds the standard "Translations" group for a list row's related-children
 * panel (§17.B14).
 *
 * Every displayed code is backed by a `_tl` companion (§8), so a page that has
 * already fetched those rows can surface them per row: the reader sees, at a
 * glance and without opening the edit modal, which locales a code is translated
 * into and which are still missing.
 *
 * This is the DESKTOP adapter — a thin map from the shared `translationItems()`
 * onto the desktop panel's item shape, where the language name is a hover
 * `title`. The mobile adapter is `src/mobile/components/mobile_translations_group.ts`;
 * both read from the same data function so the two layers cannot drift.
 *
 * The flag is decoration only: the resolved `display_text` stays the label, so
 * the row is still readable when a glyph cannot render (§17.B4).
 */
import { translationItems, type TranslationLike } from '../../lib/translation_items';
import type { RelatedChildGroup } from './RelatedChildrenPanel';

export type { TranslationLike };

/** Minimal `t` shape — avoids dragging i18next's TFunction generics into callers. */
type Translate = (key: string, defaultValue: string) => string;

/**
 * @param tl      the row's `_tl` records (safe to pass undefined/null)
 * @param t       the caller's `t` from useTranslation()
 * @param locale  the ACTIVE CSM locale, used to name each language in the
 *                reader's own language (Intl.DisplayNames)
 */
export function buildTranslationsGroup(
  tl: TranslationLike[] | null | undefined,
  t: Translate,
  locale: string,
): RelatedChildGroup {
  return {
    key: 'translations',
    title: t('common.translations_lbl', 'Translations'),
    items: translationItems(tl, locale).map(x => ({
      id: x.id,
      label: x.label,
      title: x.hover,
      icon: x.icon,
    })),
  };
}
